import { __decorate } from "tslib";
import { LitElement } from 'lit';
import { property } from 'lit/decorators.js';
import { provideContextRecord } from '../../base/context';
import { ElementDiscoveryController } from '../../base/elements';
import { DisposalBin, eventListener, listen, vdsEvent } from '../../base/events';
import { FullscreenController } from '../../base/fullscreen';
import { ElementLogger, LogLevelNameMap } from '../../base/logger';
import { RequestQueue } from '../../base/queue';
import { ScreenOrientationController } from '../../base/screen-orientation';
import { DEV_MODE } from '../../global/env';
import { clampNumber } from '../../utils/number';
import { notEqual } from '../../utils/unit';
import { CanPlay } from '../CanPlay';
import { mediaContext } from '../context';
import { MediaType } from '../MediaType';
import { ViewType } from '../ViewType';
/**
 * Base abstract media provider class that defines the interface to be implemented by
 * all concrete media providers. Extending this class enables provider-agnostic communication 💬
 *
 */
export class MediaProviderElement extends LitElement {
    constructor() {
        super();
        // -------------------------------------------------------------------------------------------
        // Lifecycle
        // -------------------------------------------------------------------------------------------
        /* c8 ignore next */
        this._logger = DEV_MODE && new ElementLogger(this);
        this._disconnectDisposal = new DisposalBin(this, 
        /* c8 ignore next */
        DEV_MODE && { name: 'disconnectDisposal', owner: this });
        /**
         * Indicates whether a user interface should be shown for controlling the resource. Set this to
         * `false` when you want to provide your own custom controls, and `true` if you want the current
         * provider to supply its own default controls. Depending on the provider, changing this prop
         * may cause the player to completely reset.
         *
         * @default false
         * @link https://developer.mozilla.org/en-US/docs/Web/API/HTMLMediaElement/controls
         */
        this.controls = false;
        /**
         * Whether media should automatically start playing from the beginning (replay) every time
         * it ends.
         *
         * @default false
         * @link https://developer.mozilla.org/en-US/docs/Web/API/HTMLMediaElement/loop
         */
        this.loop = false;
        /**
         * Whether the video is to be played "inline", that is within the element's playback area. Note
         * that setting this to `false` does not imply that the video will always be played in fullscreen.
         * Depending on the provider, changing this prop may cause the player to completely reset.
         *
         * @default false
         * @link https://developer.mozilla.org/en-US/docs/Web/HTML/Element/video#attr-playsinline
         */
        this.playsinline = false;
        this._autoplayRetryCount = 0;
        this._maxAutoplayRetries = 2;
        this._shouldMuteLastAutoplayAttempt = true;
        this._autoplayAttemptPending = false;
        this._shouldSkipNextSrcChangeReset = true;
        // -------------------------------------------------------------------------------------------
        // Context
        // -------------------------------------------------------------------------------------------
        /**
         * Any property updated inside this object will trigger a context update. The media controller
         * will provide (inject) the context record to be managed by this media provider. Any updates here
         * will flow down from the media controller to all components.
         *
         * @internal
         */
        this.ctx = provideContextRecord(this, mediaContext);
        // -------------------------------------------------------------------------------------------
        // Request Queue
        // -------------------------------------------------------------------------------------------
        /**
         * Queue actions to be applied safely after the element has connected to the DOM.
         */
        this._connectedQueue = new RequestQueue(this, 
        /* c8 ignore next */
        DEV_MODE && { name: 'connectedQueue', owner: this });
        /**
         * Queue actions to be taken on the current media provider when it's ready for playback, marked
         * by the `canPlay` property. If the media provider is ready, actions will be invoked immediately.
         */
        this.mediaRequestQueue = new RequestQueue(this, 
        /* c8 ignore next */
        DEV_MODE && { name: 'mediaRequestQueue', owner: this });
        // -------------------------------------------------------------------------------------------
        // Orientation
        // -------------------------------------------------------------------------------------------
        this.screenOrientationController = new ScreenOrientationController(this);
        // -------------------------------------------------------------------------------------------
        // Fullscreen
        // -------------------------------------------------------------------------------------------
        this.fullscreenController = new FullscreenController(this, this.screenOrientationController);
    }
    connectedCallback() {
        /* c8 ignore start */
        if (DEV_MODE) {
            if ('controller' in this) {
                // @ts-expect-error - re-init to consume log level because provider is a media controller
                // and log level context is created after this class has initialized.
                this._logger = new ElementLogger(this);
            }
            this._logMediaEvents();
        }
        /* c8 ignore stop */
        super.connectedCallback();
        new ElementDiscoveryController(this, 'vds-media-provider-connect');
        this._connectedQueue.flush();
        this._connectedQueue.serveImmediately = true;
    }
    updated(changedProperties) {
        var _a, _b, _c;
        super.updated(changedProperties);
        /* c8 ignore start */
        if (DEV_MODE) {
            changedProperties.forEach((_, prop) => {
                this._logger
                    .infoGroup(`🧬 changed prop \`${String(prop)}\` to \`${this[prop]}\``)
                    .appendWithLabel('Property', prop)
                    .appendWithLabel('Value', this[prop])
                    .end();
            });
        }
        /* c8 ignore stop */
        if (changedProperties.has('controls')) {
            this.ctx.controls = (_a = this.controls) !== null && _a !== void 0 ? _a : false;
        }
        if (changedProperties.has('loop')) {
            this.ctx.loop = (_b = this.loop) !== null && _b !== void 0 ? _b : false;
        }
        if (changedProperties.has('playsinline')) {
            this.ctx.playsinline = (_c = this.playsinline) !== null && _c !== void 0 ? _c : false;
        }
    }
    firstUpdated(changedProperties) {
        super.firstUpdated(changedProperties);
        this.ctx.canRequestFullscreen = this.canRequestFullscreen;
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        this._connectedQueue.destroy();
        this.mediaRequestQueue.destroy();
        this._shouldSkipNextSrcChangeReset = false;
        this._disconnectDisposal.empty();
    }
    // -------------------------------------------------------------------------------------------
    // Logging
    // -------------------------------------------------------------------------------------------
    /**
     * Sets the providers log level. By default it'll follow the media controller's log level. Valid
     * values in order of level include `silent`, `error`, `warn`, `info` and `debug`. Set to special
     * value `auto` for it to continue following controller.
     *
     * @default `auto`
     */
    get logLevel() {
        /* c8 ignore next */
        return DEV_MODE ? LogLevelNameMap[this._logger.level] : 'silent';
    }
    set logLevel(newLevel) {
        /* c8 ignore next */
        const numericLevel = DEV_MODE
            ? Object.values(LogLevelNameMap).findIndex((l) => l === newLevel)
            : 0;
        this._logger.level = numericLevel >= 0 ? numericLevel : undefined;
    }
    /* c8 ignore next */
    _logMediaEvents() {
        /* c8 ignore start */
        if (DEV_MODE) {
            const mediaEvents = [
                'vds-abort',
                'vds-can-play',
                'vds-can-play-through',
                'vds-controls-change',
                'vds-duration-change',
                'vds-emptied',
                'vds-ended',
                'vds-error',
                'vds-fullscreen-change',
                'vds-idle-change',
                'vds-loaded-data',
                'vds-loaded-metadata',
                'vds-load-start',
                'vds-media-type-change',
                'vds-pause',
                'vds-play',
                'vds-playing',
                'vds-progress',
                'vds-seeked',
                'vds-seeking',
                'vds-stalled',
                'vds-started',
                'vds-suspend',
                'vds-replay',
                // 'vds-time-update',
                'vds-view-type-change',
                'vds-volume-change',
                'vds-waiting'
            ];
            mediaEvents.forEach((eventType) => {
                const dispose = listen(this, eventType, (event) => {
                    this._logger
                        .infoGroup(`📡 dispatching \`${eventType}\``)
                        .appendWithLabel('Context', this.ctx)
                        .appendWithLabel('Event', event)
                        .appendWithLabel('Engine', this.engine)
                        .end();
                });
                this._disconnectDisposal.add(dispose);
            });
        }
        /* c8 ignore stop */
    }
    // -------------------------------------------------------------------------------------------
    // Properties
    // -------------------------------------------------------------------------------------------
    /**
     * Whether playback should automatically begin as soon as enough media is available to do so
     * without interruption.
     *
     * Sites which automatically play audio (or videos with an audio track) can be an unpleasant
     * experience for users, so it should be avoided when possible. If you must offer autoplay
     * functionality, you should make it opt-in (requiring a user to specifically enable it).
     *
     * However, autoplay can be useful when creating media elements whose source will be set at a
     * later time, under user control.
     *
     * @default false
     * @link https://developer.mozilla.org/en-US/docs/Web/API/HTMLMediaElement/autoplay
     */
    get autoplay() {
        return this.ctx.autoplay;
    }
    set autoplay(shouldAutoplay) {
        this.ctx.autoplay = shouldAutoplay;
        if (this.canPlay && !this._autoplayAttemptPending && shouldAutoplay) {
            this._autoplayAttemptPending = true;
            const onAttemptEnd = () => {
                this._autoplayAttemptPending = false;
            };
            this._attemptAutoplay().then(onAttemptEnd).catch(onAttemptEnd);
        }
    }
    // --
    /**
     * An `int` between `0` (silent) and `1` (loudest) indicating the audio volume. Defaults to `1`.
     *
     * @default 1
     * @link https://developer.mozilla.org/en-US/docs/Web/API/HTMLMediaElement/volume
     */
    get volume() {
        return this.canPlay ? this._getVolume() : 1;
    }
    set volume(requestedVolume) {
        this.mediaRequestQueue.queue('volume', () => {
            const volume = clampNumber(0, requestedVolume, 1);
            if (notEqual(this.volume, volume)) {
                this._setVolume(volume);
                this.requestUpdate('volume');
            }
        });
    }
    // ---
    /**
     * Whether playback should be paused. Defaults to `true` if no media has loaded or playback has
     * not started. Setting this to `false` will begin/resume playback.
     *
     * @default true
     * @link https://developer.mozilla.org/en-US/docs/Web/API/HTMLMediaElement/paused
     */
    get paused() {
        return this.canPlay ? this._getPaused() : true;
    }
    set paused(shouldPause) {
        this.mediaRequestQueue.queue('paused', () => {
            if (!shouldPause) {
                this.play();
            }
            else {
                this.pause();
            }
            this.requestUpdate('paused');
        });
    }
    // ---
    /**
     * A `double` indicating the current playback time in seconds. Defaults to `0` if the media has
     * not started to play and has not seeked. Setting this value seeks the media to the new
     * time. The value can be set to a minimum of `0` and maximum of the total length of the
     * media (indicated by the duration prop).
     *
     * @default 0
     * @link https://developer.mozilla.org/en-US/docs/Web/API/HTMLMediaElement/currentTime
     */
    get currentTime() {
        return this.canPlay ? this._getCurrentTime() : 0;
    }
    set currentTime(requestedTime) {
        this.mediaRequestQueue.queue('time', () => {
            if (notEqual(this.currentTime, requestedTime)) {
                this._setCurrentTime(requestedTime);
                this.requestUpdate('currentTime');
            }
        });
    }
    _getCurrentTime() {
        // Avoid errors where `currentTime` can have higher precision than duration.
        return Math.min(this.ctx.currentTime, this.duration);
    }
    // ---
    /**
     * Whether the audio is muted or not.
     *
     * @default false
     * @link https://developer.mozilla.org/en-US/docs/Web/API/HTMLMediaElement/muted
     */
    get muted() {
        return this.canPlay ? this._getMuted() : false;
    }
    set muted(shouldMute) {
        this.mediaRequestQueue.queue('muted', () => {
            if (notEqual(this.muted, shouldMute)) {
                this._setMuted(shouldMute);
                this.requestUpdate('muted');
            }
        });
    }
    /**
     * A snapshot of the current media state.
     */
    get mediaState() {
        return Object.assign({}, this.ctx);
    }
    /**
     * Returns an `Error` object when autoplay has failed to begin playback. This
     * can be used to determine when to show a recovery UI in the event autoplay fails.
     *
     * @default undefined
     */
    get autoplayError() {
        return this.ctx.autoplayError;
    }
    /**
     * Returns a `TimeRanges` object that indicates the ranges of the media source that the
     * browser has buffered (if any) at the moment the buffered property is accessed. This is usually
     * contiguous but if the user jumps about while media is buffering, it may contain holes.
     *
     * @link https://developer.mozilla.org/en-US/docs/Web/API/TimeRanges
     * @link https://developer.mozilla.org/en-US/docs/Web/API/HTMLMediaElement/buffered
     * @default TimeRanges
     */
    get buffered() {
        return this.ctx.buffered;
    }
    /**
     * Whether the user agent can play the media, but estimates that **not enough** data has been
     * loaded to play the media up to its end without having to stop for further buffering of
     * content.
     *
     * @default false
     * @link https://developer.mozilla.org/en-US/docs/Web/API/HTMLMediaElement/canplay_event
     */
    get canPlay() {
        return this.ctx.canPlay;
    }
    /**
     * Whether the user agent can play the media, and estimates that enough data has been
     * loaded to play the media up to its end without having to stop for further buffering
     * of content.
     *
     * @default false
     * @link https://developer.mozilla.org/en-US/docs/Web/API/HTMLMediaElement/canplaythrough_event
     */
    get canPlayThrough() {
        return this.ctx.canPlayThrough;
    }
    /**
     * The URL of the current poster. Defaults to `''` if no media/poster has been given or
     * loaded.
     *
     * @default ''
     */
    get currentPoster() {
        return this.ctx.currentPoster;
    }
    /**
     * The absolute URL of the media resource that has been chosen. Defaults to `''` if no
     * media has been loaded.
     *
     * @default ''
     * @link https://developer.mozilla.org/en-US/docs/Web/API/HTMLMediaElement/currentSrc
     */
    get currentSrc() {
        return this.ctx.currentSrc;
    }
    /**
     * A `double` indicating the total playback length of the media in seconds. If no media data is
     * available, the returned value is `NaN`. If the media is of indefinite length (such as
     * streamed live media, a WebRTC call's media, or similar), the value is `+Infinity`.
     *
     * @default NaN
     * @link https://developer.mozilla.org/en-US/docs/Web/API/HTMLMediaElement/duration
     */
    get duration() {
        return this.ctx.duration;
    }
    /**
     * Whether media playback has reached the end. In other words it'll be true
     * if `currentTime === duration`.
     *
     * @default false
     * @link https://developer.mozilla.org/en-US/docs/Web/API/HTMLMediaElement/ended
     */
    get ended() {
        return this.ctx.ended;
    }
    /**
     * Contains the most recent error or undefined if there's been none. You can listen for
     * `vds-error` event updates and examine this object to debug further. The error could be a
     * native `MediaError` object or something else.
     *
     * @default undefined
     * @link https://developer.mozilla.org/en-US/docs/Web/API/HTMLMediaElement/error
     */
    get error() {
        return this.ctx.error;
    }
    /**
     * Whether the current media is a live stream.
     *
     * @default false
     */
    get live() {
        return this.ctx.mediaType === MediaType.LiveVideo;
    }
    /**
     * The type of media that is currently active, whether it's audio or video. Defaults
     * to `unknown` when no media has been loaded or the type cannot be determined.
     *
     * @default MediaType.Unknown
     */
    get mediaType() {
        return this.ctx.mediaType;
    }
    /**
     * Contains the ranges of the media source that the browser has played, if any.
     *
     * @default TimeRanges
     */
    get played() {
        return this.ctx.played;
    }
    /**
     * Whether media is actively playing back. Defaults to `false` if no media has
     * loaded or playback has not started.
     *
     * @default false
     */
    get playing() {
        return this.ctx.playing;
    }
    /**
     * Contains the time ranges that the user is able to seek to, if any. This tells us which parts
     * of the media can be played without delay; this is irrespective of whether that part has
     * been downloaded or not.
     *
     * Some parts of the media may be seekable but not buffered if byte-range
     * requests are enabled on the server. Byte range requests allow parts of the media file to
     * be delivered from the server and so can be ready to play almost immediately — thus they are
     * seekable.
     *
     * @default TimeRanges
     * @link https://developer.mozilla.org/en-US/docs/Web/API/TimeRanges
     * @link https://developer.mozilla.org/en-US/docs/Web/API/HTMLMediaElement/seekable
     */
    get seekable() {
        return this.ctx.seekable;
    }
    /**
     * Whether media is actively seeking to an new playback position.
     *
     * @default false
     */
    get seeking() {
        return this.ctx.seeking;
    }
    /**
     * Whether media playback has started. In other words it will be true if `currentTime > 0`.
     *
     * @default false
     */
    get started() {
        return this.ctx.started;
    }
    /**
     * The type of player view that is being used, whether it's an audio player view or
     * video player view. Normally if the media type is of audio then the view is of type audio, but
     * in some cases it might be desirable to show a different view type. For example, when playing
     * audio with a poster. This is subject to the provider allowing it. Defaults to `unknown`
     * when no media has been loaded.
     *
     * @default ViewType.Unknown
     */
    get viewType() {
        return this.ctx.viewType;
    }
    /**
     * Whether playback has temporarily stopped because of a lack of temporary data.
     *
     * @default false
     */
    get waiting() {
        return this.ctx.waiting;
    }
    /**
     * Determines if the media provider "should" play the given type. "Should" in this
     * context refers to the `canPlayType()` method returning `Maybe` or `Probably`.
     *
     * @param type refer to `canPlayType`.
     */
    shouldPlayType(type) {
        const canPlayType = this.canPlayType(type);
        return canPlayType === CanPlay.Maybe || canPlayType === CanPlay.Probably;
    }
    /**
     * @throws {Error} - Will throw if media is not ready for playback.
     */
    _throwIfNotReadyForPlayback() {
        if (!this.canPlay) {
            throw Error(`Media is not ready - wait for \`vds-can-play\` event.`);
        }
    }
    async _resetPlayback() {
        this._setCurrentTime(0);
    }
    async _resetPlaybackIfEnded() {
        if (!this.ended || this.currentTime === 0)
            return;
        return this._resetPlayback();
    }
    /**
     * @throws {Error} - Will throw if player is not in a video view.
     */
    _throwIfNotVideoView() {
        if (this.viewType !== ViewType.Video) {
            throw Error('Player is currently not in a video view.');
        }
    }
    async _handleMediaReady(event) {
        if (this.canPlay)
            return;
        this.ctx.canPlay = true;
        this.dispatchEvent(vdsEvent('vds-can-play', { originalEvent: event }));
        await this.mediaRequestQueue.flush();
        this.mediaRequestQueue.serveImmediately = true;
        /* c8 ignore start */
        if (DEV_MODE) {
            this._logger
                .infoGroup('-------------------------- ✅ MEDIA READY -----------------------------------')
                .appendWithLabel('Context', this.mediaState)
                .appendWithLabel('Event', event)
                .appendWithLabel('Engine', this.engine)
                .end();
        }
        /* c8 ignore stop */
        this._autoplayRetryCount = 0;
        this.ctx.autoplayError = undefined;
        this._autoplayAttemptPending = true;
        await this._attemptAutoplay();
        this._autoplayAttemptPending = false;
    }
    async _attemptAutoplay() {
        if (!this.canPlay ||
            !this.autoplay ||
            this.started ||
            this._autoplayRetryCount >= this._maxAutoplayRetries) {
            return;
        }
        // On last attempt try muted.
        const shouldTryMuted = !this.muted &&
            this._shouldMuteLastAutoplayAttempt &&
            this._autoplayRetryCount === this._maxAutoplayRetries - 1;
        /* c8 ignore start */
        if (DEV_MODE && this._autoplayRetryCount > 0) {
            this._logger
                .warnGroup(`Seems like autoplay has failed, retrying [attempt ${this._autoplayRetryCount}]${shouldTryMuted ? ' muted' : ''}...`)
                .appendWithLabel('Engine', this.engine)
                .end();
        }
        /* c8 ignore stop */
        let didAttemptSucceed = false;
        try {
            if (shouldTryMuted)
                this.muted = true;
            await this.play();
            didAttemptSucceed = true;
            /* c8 ignore start */
            if (DEV_MODE) {
                this._logger.info('✅ Autoplay was successful.');
            }
            /* c8 ignore stop */
        }
        catch (error) {
            /* c8 ignore start */
            if (DEV_MODE) {
                this._logger
                    .errorGroup(`Autoplay retry [attempt ${this._autoplayRetryCount} out of ${this._maxAutoplayRetries}] failed.`)
                    .appendWithLabel('Engine', this.engine)
                    .appendWithLabel('Error', error)
                    .end();
            }
            /* c8 ignore stop */
            if (this._autoplayRetryCount === this._maxAutoplayRetries - 1) {
                this.ctx.autoplayError = error;
                this.requestUpdate();
            }
        }
        if (!didAttemptSucceed) {
            this._autoplayRetryCount += 1;
            return this._attemptAutoplay();
        }
    }
    _handleMediaSrcChange() {
        if (!this.hasUpdated)
            return;
        // Skip first flush to ensure initial properties set make it to the provider.
        if (this._shouldSkipNextSrcChangeReset) {
            this._shouldSkipNextSrcChangeReset = false;
            return;
        }
        /* c8 ignore start */
        if (DEV_MODE) {
            this._logger
                .infoGroup('📼 media src change')
                .appendWithLabel('Current src', this.currentSrc)
                .appendWithLabel('Engine', this.engine)
                .end();
        }
        /* c8 ignore stop */
        this.mediaRequestQueue.serveImmediately = false;
        this._softResetMediaContext();
    }
    /**
     * Media context properties that should be reset when media is changed. Override this
     * to include additional properties.
     */
    _getMediaPropsToResetWhenSrcChanges() {
        return new Set([
            'autoplayError',
            'buffered',
            'buffering',
            'canPlay',
            'canPlayThrough',
            'currentSrc',
            'currentTime',
            'duration',
            'ended',
            'mediaType',
            'paused',
            'canPlay',
            'played',
            'playing',
            'seekable',
            'seeking',
            'started',
            'waiting'
        ]);
    }
    /**
     * When the `currentSrc` is changed this method is called to update any context properties
     * that need to be reset. Important to note that not all properties are reset, only the
     * properties returned from `getSoftResettableMediaContextProps()`.
     */
    _softResetMediaContext() {
        const propsToReset = this._getMediaPropsToResetWhenSrcChanges();
        /* c8 ignore start */
        if (DEV_MODE) {
            this._logger
                .infoGroup('soft context reset')
                .appendWithLabel('Engine', this.engine)
                .appendWithLabel('Context', this.mediaState)
                .appendWithLabel('Reset props', propsToReset)
                .end();
        }
        /* c8 ignore stop */
        Object.keys(mediaContext).forEach((prop) => {
            if (propsToReset.has(prop)) {
                this.ctx[prop] = mediaContext[prop].initialValue;
            }
        });
    }
    /**
     * Whether the native browser fullscreen API is available, or the current provider can
     * toggle fullscreen mode. This does not mean that the operation is guaranteed to be successful,
     * only that it can be attempted.
     *
     * @default false
     * @link https://developer.mozilla.org/en-US/docs/Web/API/Fullscreen_API
     */
    get canRequestFullscreen() {
        return this.fullscreenController.isSupported;
    }
    /**
     * Whether the player is currently in fullscreen mode.
     *
     * @default false
     */
    get fullscreen() {
        return this.ctx.fullscreen;
    }
    /**
     * This will indicate the orientation to lock the screen to when in fullscreen mode and
     * the Screen Orientation API is available. The default is `undefined` which indicates
     * no screen orientation change.
     */
    get fullscreenOrientation() {
        return this.fullscreenController.screenOrientationLock;
    }
    set fullscreenOrientation(lockType) {
        this.fullscreenController.screenOrientationLock = lockType;
    }
    async requestFullscreen() {
        if (this.fullscreenController.isRequestingNativeFullscreen) {
            return super.requestFullscreen();
        }
        return this.fullscreenController.requestFullscreen();
    }
    exitFullscreen() {
        return this.fullscreenController.exitFullscreen();
    }
    _handleFullscreenChange(event) {
        /* c8 ignore start */
        if (DEV_MODE) {
            this._logger
                .infoGroup('fullscreen change')
                .appendWithLabel('Event', event)
                .appendWithLabel('Engine', this.engine)
                .end();
        }
        /* c8 ignore stop */
        this.ctx.fullscreen = event.detail;
    }
}
__decorate([
    property({ attribute: 'log-level' })
], MediaProviderElement.prototype, "logLevel", null);
__decorate([
    property({ type: Boolean, reflect: true })
], MediaProviderElement.prototype, "autoplay", null);
__decorate([
    property({ type: Boolean, reflect: true })
], MediaProviderElement.prototype, "controls", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], MediaProviderElement.prototype, "loop", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], MediaProviderElement.prototype, "playsinline", void 0);
__decorate([
    property({ type: Number, reflect: true })
], MediaProviderElement.prototype, "volume", null);
__decorate([
    property({ type: Boolean, reflect: true })
], MediaProviderElement.prototype, "paused", null);
__decorate([
    property({ attribute: 'current-time', type: Number })
], MediaProviderElement.prototype, "currentTime", null);
__decorate([
    property({ type: Boolean, reflect: true })
], MediaProviderElement.prototype, "muted", null);
__decorate([
    property({ attribute: 'fullscreen-orientation' })
], MediaProviderElement.prototype, "fullscreenOrientation", null);
__decorate([
    eventListener('vds-fullscreen-change')
], MediaProviderElement.prototype, "_handleFullscreenChange", null);
